import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from "../../components/ui/alert-dialog";
import {
  Inbox, Search, UserPlus, Phone, Calendar, MapPin, Users as UsersIcon,
  Trash2, Eye, ArrowRight, CheckCircle, XCircle, Clock, Package
} from "lucide-react";
import PageHeader from "../../components/PageHeader";
import StatusBadge from "../../components/StatusBadge";

const Solicitudes = () => {
  const navigate = useNavigate();
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filtro, setFiltro] = useState("todas");
  const [deleteId, setDeleteId] = useState(null);
  const [converting, setConverting] = useState(null);
  const [detalle, setDetalle] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/solicitudes`);
      setSolicitudes(res.data);
    } catch {
      toast.error("Error al cargar solicitudes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleConvertir = async (sol) => {
    setConverting(sol.solicitud_id);
    try {
      const res = await axios.post(`${API}/solicitudes/${sol.solicitud_id}/convertir`);
      const msg = res.data.cliente_reused
        ? `Cliente existente reutilizado${res.data.cotizacion_id ? " + cotización vinculada" : ""}`
        : `Cliente creado${res.data.cotizacion_id ? " + cotización formalizada" : ""}`;
      toast.success(msg);
      await fetchData();
      if (res.data.cotizacion_id) {
        navigate(`/panel/cotizaciones`);
      } else {
        navigate(`/panel/clientes/${res.data.cliente_id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al convertir");
    } finally {
      setConverting(null);
    }
  };

  const handleCambiarEstatus = async (solId, estatus) => {
    try {
      await axios.put(`${API}/solicitudes/${solId}`, { estatus });
      toast.success("Estatus actualizado");
      await fetchData();
    } catch { toast.error("Error"); }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/solicitudes/${deleteId}`);
      toast.success("Solicitud eliminada");
      await fetchData();
    } catch { toast.error("Error"); }
    finally { setDeleteId(null); }
  };

  const filtered = solicitudes.filter(s => {
    const matchSearch = s.nombre?.toLowerCase().includes(search.toLowerCase()) ||
      s.tipo_evento?.toLowerCase().includes(search.toLowerCase()) ||
      s.whatsapp?.includes(search);
    const matchFiltro = filtro === "todas" || s.estatus === filtro;
    return matchSearch && matchFiltro;
  });

  const estatusConfig = {
    nueva: { label: "Nueva", color: "bg-blue-100 text-blue-800", icon: Clock },
    contactada: { label: "Contactada", color: "bg-amber-100 text-amber-800", icon: Phone },
    convertida: { label: "Convertida", color: "bg-emerald-100 text-emerald-800", icon: CheckCircle },
    descartada: { label: "Descartada", color: "bg-red-100 text-red-800", icon: XCircle },
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-emerald-800 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl text-stone-900" data-testid="solicitudes-title">Solicitudes</h1>
          <p className="text-sm text-stone-500">Leads desde la web ({filtered.length})</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input
            placeholder="Buscar por nombre, evento o WhatsApp..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 border-stone-300"
            data-testid="search-solicitudes"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {["todas", "nueva", "contactada", "convertida", "descartada"].map(f => (
            <Button key={f} size="sm" variant={filtro === f ? "default" : "outline"}
              onClick={() => setFiltro(f)}
              className={filtro === f ? "btn-primary" : "border-stone-300"}
              data-testid={`filtro-${f}`}
            >
              {f === "todas" ? "Todas" : estatusConfig[f]?.label}
            </Button>
          ))}
        </div>
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div className="text-center py-16">
          <Inbox className="w-16 h-16 mx-auto text-stone-300 mb-4" />
          <h3 className="text-lg font-medium text-stone-700">No hay solicitudes</h3>
          <p className="text-sm text-stone-500">Los clientes pueden enviar solicitudes desde tu pagina web</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filtered.map(sol => {
            return (
              <Card key={sol.solicitud_id} className="border-stone-200 hover:border-emerald-300 transition-colors" data-testid={`solicitud-${sol.solicitud_id}`}>
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row justify-between gap-3">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-3 flex-wrap">
                        <h3 className="font-medium text-stone-900 text-base">{sol.nombre}</h3>
                        <StatusBadge status={sol.estatus} />
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-stone-600">
                        <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {sol.whatsapp}</span>
                        <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {sol.fecha_evento || "Sin fecha"}</span>
                        <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {sol.tipo_evento}</span>
                        {sol.num_invitados && <span className="flex items-center gap-1"><UsersIcon className="w-3.5 h-3.5" /> {sol.num_invitados} inv.</span>}
                      </div>
                      {sol.servicios_seleccionados?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          <Package className="w-3.5 h-3.5 text-stone-400 mt-0.5" />
                          {sol.servicios_seleccionados.map((s) => (
                            <span key={`${s.nombre}-${s.precio}`} className="bg-stone-100 text-stone-700 px-2 py-0.5 rounded text-xs">
                              {s.nombre} {s.precio ? `$${Number(s.precio).toLocaleString()}` : ""}
                            </span>
                          ))}
                        </div>
                      )}
                      {sol.mensaje && <p className="text-xs text-stone-500 italic mt-1">&quot;{sol.mensaje}&quot;</p>}
                      <p className="text-xs text-stone-400">{new Date(sol.created_at).toLocaleDateString("es-MX", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</p>
                    </div>
                    <div className="flex sm:flex-col gap-2 shrink-0">
                      {sol.estatus !== "convertida" && (
                        <Button
                          size="sm"
                          className="btn-primary text-xs"
                          onClick={() => handleConvertir(sol)}
                          disabled={converting === sol.solicitud_id}
                          data-testid={`btn-convertir-${sol.solicitud_id}`}
                        >
                          {converting === sol.solicitud_id ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-1" />
                          ) : (
                            <UserPlus className="w-3.5 h-3.5 mr-1" />
                          )}
                          Convertir
                        </Button>
                      )}
                      {sol.estatus === "convertida" && sol.cliente_id && (
                        <Button size="sm" variant="outline" className="border-stone-300 text-xs"
                          onClick={() => navigate(`/panel/clientes/${sol.cliente_id}`)}
                          data-testid={`btn-ver-cliente-${sol.solicitud_id}`}
                        >
                          <Eye className="w-3.5 h-3.5 mr-1" /> Ver Cliente
                        </Button>
                      )}
                      {sol.estatus === "nueva" && (
                        <Button size="sm" variant="outline" className="border-amber-300 text-amber-700 text-xs"
                          onClick={() => handleCambiarEstatus(sol.solicitud_id, "contactada")}
                        >
                          <Phone className="w-3.5 h-3.5 mr-1" /> Contactada
                        </Button>
                      )}
                      {sol.estatus !== "descartada" && sol.estatus !== "convertida" && (
                        <Button size="sm" variant="outline" className="border-red-300 text-red-600 text-xs"
                          onClick={() => handleCambiarEstatus(sol.solicitud_id, "descartada")}
                        >
                          <XCircle className="w-3.5 h-3.5 mr-1" /> Descartar
                        </Button>
                      )}
                      <Button size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50 text-xs"
                        onClick={() => setDeleteId(sol.solicitud_id)}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar solicitud</AlertDialogTitle>
            <AlertDialogDescription>Esta accion no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Solicitudes;
